import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { RoomEvent } from 'livekit-client';
import logo from '../images/icon_small.png';
// The themed radial backdrop every other full-screen Cyclone surface sits on
// (Home, Auth, SignIn). Importing it here is what makes a meeting inherit the
// user's theme - including ones with a backdrop image - instead of being an
// island with its own flat background.
import '../style/parallax-star.css';
import '../style/Meeting.css';

import {
    getMeetingInfo, joinMeeting, endMeeting, removeMeetingParticipant,
    startMeetingTranscript, stopMeetingTranscript
} from '../api/MeetingAPI';
import { useVoiceConnection } from '../hooks/useVoiceConnection';
import { useMeetingSignals } from '../hooks/useMeetingSignals';
import { useRoomEnergy } from '../hooks/useRoomEnergy';
import { useBackgroundEffect } from '../hooks/useBackgroundEffect';
import { useVideoQuality } from '../hooks/useVideoQuality';
import { sceneById } from '../utils/meetingScenes';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useLanguage } from '../i18n/LanguageContext';
import { getMicDefaultOn } from '../utils/voiceSettings';
import MeetingLobby from '../components/meeting/MeetingLobby';
import MeetingTopBar from '../components/meeting/MeetingTopBar';
import MeetingStage from '../components/meeting/MeetingStage';
import MeetingControlBar from '../components/meeting/MeetingControlBar';
import MeetingSidePanel from '../components/meeting/MeetingSidePanel';
import MeetingReactions from '../components/meeting/MeetingReactions';
import MeetingStarfield from '../components/meeting/MeetingStarfield';

const token = () => localStorage.getItem('accessToken');

// Electron exposes its own screen picker (see electron-starter.js's
// setDisplayMediaRequestHandler); a desktop browser has the native one. Mobile
// browsers have neither, so the share button is hidden there rather than
// offered and guaranteed to fail.
function screenShareAvailable() {
    if (window.cyclone) return true;
    return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
}

// How long after the host's own "end for everyone" we wait before deciding a
// disconnect was the meeting ending rather than a network blip.
const END_CHECK_DELAY_MS = 600;

// The standalone meeting page. Mounted outside Home's shell and outside
// SocketProvider on purpose: a meeting has to work for someone who is not
// signed in, has no socket, and may never have heard of Cyclone - so nothing
// on this route may depend on app state. The only Cyclone surface left is the
// logo and, once the call is over, an invitation to go look at the app.
export default function Meeting() {
    const { code } = useParams();
    const history = useHistory();
    const { t } = useLanguage();

    // 'loading' -> 'lobby' -> 'joining' -> 'in' -> 'left' | 'ended' | 'error'
    const [phase, setPhase] = useState('loading');
    const [meeting, setMeeting] = useState(null);
    const [lobbyParticipants, setLobbyParticipants] = useState([]);
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);

    const [credentials, setCredentials] = useState(null); // { token, identity, isHost, displayName }
    const [lobbyMicOn, setLobbyMicOn] = useState(() => getMicDefaultOn());
    const [lobbyCameraOn, setLobbyCameraOn] = useState(false);

    const [room, setRoom] = useState(null);
    const [pinnedKey, setPinnedKey] = useState(null);
    const [panel, setPanel] = useState(null); // null | 'chat' | 'people' | 'transcript'
    const [notice, setNotice] = useState(null);
    const [transcriptBusy, setTranscriptBusy] = useState(false);

    // Set just before an intentional teardown (leaving, or the host ending the
    // meeting) so the Disconnected handler can tell "we meant that" from "the
    // connection dropped" and not misreport one as the other.
    const intentionalExitRef = useRef(null);

    // Passed fresh every render; useVoiceConnection keeps these in a ref and
    // only reads them at the moment it establishes a connection, so a new
    // object here never re-triggers one. The lobby's mic/camera choices are
    // therefore honoured on join and then superseded by the in-call buttons.
    const voice = useVoiceConnection(
        credentials ? credentials.token : null,
        // Keys the connection effect. The LiveKit room name is derived from
        // the code, so this changes only when the meeting does.
        `meet:${code}`,
        phase === 'in',
        // spatialAudio is meeting-only: a group room's voice panel has no
        // stable on-screen position per speaker to derive a direction from.
        { micDefaultOn: lobbyMicOn, cameraDefaultOn: lobbyCameraOn, onRoom: setRoom, spatialAudio: true }
    );

    const signals = useMeetingSignals(room, credentials ? credentials.identity : '');
    const background = useBackgroundEffect(room, voice.cameraOn);
    const videoQuality = useVideoQuality(room, voice.cameraOn);

    // Together mode: when the host puts everyone in a scene, each client
    // switches its own camera to that background. The compositing is the
    // shared backdrop plus the seamless stage layout, not a true cut-out of
    // everyone onto one canvas - that would need each participant's
    // segmentation mask on every other participant's machine, and nothing
    // carries an alpha channel over WebRTC.
    const { sharedScene } = signals;
    const { selectEffect: selectBackground } = background;
    useEffect(() => {
        if (!sharedScene) return;
        const scene = sceneById(sharedScene);
        if (scene) selectBackground({ kind: 'image', src: scene.src, id: scene.id });
    }, [sharedScene, selectBackground]);

    // The ambient backdrop and the analog speaking glow both come from here.
    // Skipped entirely on a phone and under a theme with animated backgrounds
    // turned off - the starfield was removed from the app for exactly those
    // reasons, and reviving it must not undo that.
    const { isMobile } = useLayoutMode();
    const ambientAllowed = !isMobile && document.documentElement.getAttribute('data-bg-animation') !== 'off';
    const { energy, levels } = useRoomEnergy(room, { enabled: phase === 'in' && ambientAllowed });

    const loadInfo = useCallback(async () => {
        const response = await getMeetingInfo(code, token());
        if (response.status !== 'success') {
            setError(response.message || t('meeting.errors.notFound'));
            setPhase(response.code === 'ENDED' ? 'ended' : 'error');
            return null;
        }
        setMeeting(response.data.meeting);
        setLobbyParticipants(response.data.participants || []);
        setMe(response.data.me);
        return response.data;
    }, [code, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await loadInfo();
            if (cancelled || !data) return;
            if (data.meeting.status !== 'active') { setPhase('ended'); return; }
            setPhase('lobby');
        })();
        return () => { cancelled = true; };
    }, [loadInfo]);

    // Keeps the lobby's "who is already inside" list honest while someone sits
    // on it deciding - the backend reads this straight from LiveKit, so there
    // is no push channel to subscribe to before joining.
    useEffect(() => {
        if (phase !== 'lobby') return undefined;
        const interval = setInterval(() => { loadInfo(); }, 8000);
        return () => clearInterval(interval);
    }, [phase, loadInfo]);

    const handleJoin = useCallback(async (displayName) => {
        setPhase('joining');
        setError(null);
        const response = await joinMeeting(code, displayName, token());
        if (response.status !== 'success') {
            setError(response.message || t('meeting.errors.joinFailed'));
            setPhase(response.code === 'ENDED' ? 'ended' : 'lobby');
            return;
        }
        setMeeting(response.data.meeting);
        setCredentials({
            token: response.data.token,
            identity: response.data.identity,
            isHost: response.data.isHost,
            displayName: response.data.displayName
        });
        setPhase('in');
    }, [code, t]);

    const leave = useCallback(() => {
        intentionalExitRef.current = 'left';
        setCredentials(null);
        setPhase('left');
    }, []);

    const handleEndMeeting = useCallback(async () => {
        const response = await endMeeting(token(), code);
        if (response.status !== 'success') {
            setNotice(response.message || t('meeting.errors.endFailed'));
            return;
        }
        intentionalExitRef.current = 'ended';
        setCredentials(null);
        setPhase('ended');
    }, [code, t]);

    // Host-only (the backend re-checks). The resulting state reaches everyone
    // over the bot's own data channel, not from here - this call only flips
    // the switch.
    const handleToggleTranscript = useCallback(async (start) => {
        setTranscriptBusy(true);
        const response = start
            ? await startMeetingTranscript(token(), code)
            : await stopMeetingTranscript(token(), code);
        setTranscriptBusy(false);
        if (response.status !== 'success') {
            setNotice(response.message || t('meeting.transcript.failed'));
        }
    }, [code, t]);

    const handleRemove = useCallback(async (identity, name) => {
        const response = await removeMeetingParticipant(token(), code, identity);
        setNotice(response.status === 'success'
            ? t('meeting.removedNotice', { name })
            : (response.message || t('meeting.errors.removeFailed')));
    }, [code, t]);

    // Disconnects we did not ask for. The common causes are the host ending
    // the meeting (postEndMeeting deletes the LiveKit room out from under
    // everyone) and being removed - neither of which sends us anything, since
    // the data channel dies with the connection. So ask the backend what
    // happened rather than guessing.
    useEffect(() => {
        if (phase !== 'in' || !room) return undefined;
        const handleDisconnected = async () => {
            if (intentionalExitRef.current) return;
            setCredentials(null);
            // Give the backend a moment to have committed the status change
            // before reading it, so a host's end doesn't momentarily look like
            // a removal to everyone else.
            await new Promise((resolve) => setTimeout(resolve, END_CHECK_DELAY_MS));
            const response = await getMeetingInfo(code, token());
            if (response.status === 'success' && response.data.meeting.status === 'active') {
                // Still running, so this was about us specifically - either
                // removed by the host, or a connection we lost. Back to the
                // lobby, where rejoining is one click.
                setMeeting(response.data.meeting);
                setLobbyParticipants(response.data.participants || []);
                setError(t('meeting.errors.disconnected'));
                setPhase('lobby');
                return;
            }
            setPhase('ended');
        };
        room.on(RoomEvent.Disconnected, handleDisconnected);
        return () => { room.off(RoomEvent.Disconnected, handleDisconnected); };
    }, [phase, room, code, t]);

    // Messages that land while the chat tab is already open are read on
    // arrival - otherwise the badge would count them and only clear on the
    // next tab switch.
    const { unreadCount, markChatRead } = signals;
    useEffect(() => {
        if (panel === 'chat' && unreadCount > 0) markChatRead();
    }, [panel, unreadCount, markChatRead]);

    useEffect(() => {
        if (!notice) return undefined;
        const timeout = setTimeout(() => setNotice(null), 4000);
        return () => clearTimeout(timeout);
    }, [notice]);

    // Warns before a browser tab close while in a call, the same way Meet
    // does - closing the tab silently drops you out of a conversation.
    useEffect(() => {
        if (phase !== 'in') return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [phase]);

    // A guest who has just left a meeting has no session, and /home bounces
    // straight back to the login screen - send them there directly instead of
    // through a redirect that looks like a glitch.
    const goToCyclone = () => history.push(localStorage.getItem('accessToken') ? '/home/groups' : '/login');

    if (phase === 'loading') {
        return (
            <div className="parallax-back MeetFullMessage">
                <div className="MeetMessageCard">
                    <img src={logo} alt="" className="MeetMessageLogo BrandFallbackImg" />
                    <p>{t('meeting.loading')}</p>
                </div>
            </div>
        );
    }

    if (phase === 'error') {
        return (
            <div className="parallax-back MeetFullMessage">
                <div className="MeetMessageCard">
                    <img src={logo} alt="" className="MeetMessageLogo BrandFallbackImg" />
                    <h1>{t('meeting.errors.notFoundTitle')}</h1>
                    <p>{error}</p>
                    <button className="primaryColor MeetMessageBtn" onClick={goToCyclone}>{t('meeting.goToCyclone')}</button>
                </div>
            </div>
        );
    }

    if (phase === 'ended' || phase === 'left') {
        const isEnded = phase === 'ended';
        return (
            <div className="parallax-back MeetFullMessage">
                <div className="MeetMessageCard">
                    <img src={logo} alt="" className="MeetMessageLogo BrandFallbackImg" />
                    <h1>{isEnded ? t('meeting.endedTitle') : t('meeting.leftTitle')}</h1>
                    <p>{isEnded ? t('meeting.endedBody') : t('meeting.leftBody')}</p>
                    <div className="MeetMessageActions">
                        {!isEnded && (
                            <button
                                className="secondaryColor MeetMessageBtn"
                                onClick={() => { intentionalExitRef.current = null; setError(null); setPhase('lobby'); loadInfo(); }}
                            >
                                {t('meeting.rejoin')}
                            </button>
                        )}
                        <button className="primaryColor MeetMessageBtn" onClick={goToCyclone}>{t('meeting.goToCyclone')}</button>
                    </div>
                    <p className="MeetMessageFootnote">{t('meeting.cyclonePitch')}</p>
                </div>
            </div>
        );
    }

    if (phase === 'lobby' || phase === 'joining') {
        return (
            <MeetingLobby
                meeting={meeting}
                participants={lobbyParticipants}
                defaultName={me ? me.displayName : ''}
                nameLocked={!!me}
                micOn={lobbyMicOn}
                cameraOn={lobbyCameraOn}
                onToggleMic={setLobbyMicOn}
                onToggleCamera={setLobbyCameraOn}
                onJoin={handleJoin}
                joining={phase === 'joining'}
                error={error}
            />
        );
    }

    // Reachable for a beat during teardown: the disconnect handler clears
    // credentials and the phase change that unmounts this view lands on the
    // next render, so without this the stage dereferences a null identity and
    // takes the whole page down with it - which is what a dropped connection
    // used to look like.
    if (!credentials) {
        return (
            <div className="parallax-back MeetFullMessage">
                <div className="MeetMessageCard">
                    <img src={logo} alt="" className="MeetMessageLogo BrandFallbackImg" />
                    <p>{t('meeting.loading')}</p>
                </div>
            </div>
        );
    }

    const isHost = credentials.isHost;
    const humans = voice.participants.filter((p) => !p.identity.startsWith('bot:'));

    return (
        <div className={`parallax-back MeetRoot ${panel ? 'MeetRootWithPanel' : ''}`}>
            {ambientAllowed && <MeetingStarfield energy={energy} />}

            <MeetingTopBar
                meeting={meeting}
                participantCount={humans.length}
                onOpenParticipants={() => setPanel('people')}
            />

            <div className="MeetBody">
                <MeetingStage
                    participants={voice.participants}
                    videoTracks={voice.videoTracks}
                    screenShareTracks={voice.screenShareTracks}
                    screenShareAudioTracks={voice.screenShareAudioTracks}
                    pinnedKey={pinnedKey}
                    onTogglePin={(key) => setPinnedKey((current) => (current === key ? null : key))}
                    raisedHands={signals.raisedHands}
                    audioLevels={levels}
                    onVolumeChange={voice.setParticipantVolume}
                    canRemove={isHost}
                    onRemove={handleRemove}
                    annotations={signals.annotations}
                    localIdentity={credentials.identity}
                    onStroke={signals.sendStroke}
                    onPointer={signals.sendPointer}
                    onClearAnnotations={signals.clearAnnotations}
                    onPanChange={voice.setParticipantPan}
                    togetherMode={!!signals.sharedScene}
                />

                {panel && (
                    <MeetingSidePanel
                        tab={panel}
                        onTabChange={(next) => { setPanel(next); if (next === 'chat') signals.markChatRead(); }}
                        onClose={() => setPanel(null)}
                        messages={signals.messages}
                        onSendMessage={signals.sendMessage}
                        localIdentity={credentials.identity}
                        participants={humans}
                        raisedHands={signals.raisedHands}
                        isHost={isHost}
                        onRemove={handleRemove}
                        meeting={meeting}
                        transcript={signals.transcript}
                        onToggleTranscript={handleToggleTranscript}
                        transcriptBusy={transcriptBusy}
                    />
                )}
            </div>

            <MeetingReactions reactions={signals.reactions} />

            {(notice || voice.error) && (
                <div className="MeetNotice glass">
                    {notice || (voice.error === 'SCREEN_PERMISSION_DENIED' ? t('meeting.errors.screenPermission') : voice.error)}
                </div>
            )}

            <MeetingControlBar
                muted={voice.muted}
                onToggleMute={voice.toggleMute}
                audioDevices={voice.devices}
                selectedAudioDeviceId={voice.selectedDeviceId}
                onSelectAudioDevice={voice.selectDevice}
                cameraOn={voice.cameraOn}
                onToggleCamera={voice.toggleCamera}
                videoDevices={voice.videoDevices}
                selectedVideoDeviceId={voice.selectedVideoDeviceId}
                onSelectVideoDevice={voice.selectVideoDevice}
                screenShareOn={voice.screenShareOn}
                onToggleScreenShare={voice.toggleScreenShare}
                screenShareAvailable={screenShareAvailable()}
                background={background}
                onSelectBackground={background.selectEffect}
                sharedScene={signals.sharedScene}
                onShareScene={(effect) => signals.shareScene(effect ? effect.id : null)}
                handRaised={signals.handRaised}
                onToggleHand={signals.toggleHand}
                onReaction={signals.sendReaction}
                chatOpen={panel === 'chat'}
                onToggleChat={() => { const open = panel === 'chat'; setPanel(open ? null : 'chat'); if (!open) signals.markChatRead(); }}
                unreadCount={signals.unreadCount}
                participantsOpen={panel === 'people'}
                onToggleParticipants={() => setPanel((current) => (current === 'people' ? null : 'people'))}
                transcriptOpen={panel === 'transcript'}
                onToggleTranscriptPanel={() => setPanel((current) => (current === 'transcript' ? null : 'transcript'))}
                transcriptStatus={signals.transcript.status}
                participantCount={humans.length}
                videoQuality={videoQuality}
                pinned={!!pinnedKey}
                onClearPin={() => setPinnedKey(null)}
                isHost={isHost}
                onEndMeeting={handleEndMeeting}
                onLeave={leave}
            />
        </div>
    );
}
